import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useParams, useNavigate } from 'react-router-dom';

import { TeamGroup, TeamMember, User } from '@tassomutuo-backoffice/types';
import {
  getAllTeams,
  getTeamMembers,
  createTeamGroup as createTeamGroupRequest,
  addMemberToGroup,
  removeMemberFromGroup,
  removeTeamGroup as removeTeamGroupRequest,
  getAllTeamGroup,
  removeMemberFromTeam,
  sendNotificationAlertTeamMember,
} from '../../../../services/api/team/team';
import { changeCanReceiveLeads, changeCanReceiveLeadsQuinto, changeCanReceiveLeadsPrestito } from '../../../../services/api/users/user';
import { useUserStore } from '../../../../store/user';
import { selectUser } from '../../../../store/user/userSelectors';

const defaultPageModel = {
  page: 0,
  pageSize: 10,
};

type ModalState = {
  userToChangeCanReceiveLeads: User | null;
  userToChangeCanReceiveLeadsQuinto: User | null;
  userToChangeCanReceiveLeadsPrestito: User | null;
  addMemberToTeamGroup: { teamGroup: TeamGroup; user: User } | null;
  removeMemberFromTeamGroup: { teamGroup: TeamGroup; user: User } | null;
  removeTeamGroup: TeamGroup | null;
  removeMemberFromTeam: User | null;
};

type ModalConfig = {
  title: string;
  description: string;
  onConfirm: () => Promise<void>;
};

const useTeamsDetailPage = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useUserStore(selectUser);

  const [selectedTeamOption, setSelectedTeamOption] = useState<{
    value: string;
    label: string;
  } | null>(null);

  const [membersTableState, setMembersTableState] = useState({
    pagination: defaultPageModel,
    filterEmail: '',
  });

  const [showModalAlertNotify, setShowModalAlertNotify] = useState(false);

  const [modalState, setModalState] = useState<ModalState>({
    userToChangeCanReceiveLeads: null as User | null,
    userToChangeCanReceiveLeadsQuinto: null as User | null,
    userToChangeCanReceiveLeadsPrestito: null as User | null,
    addMemberToTeamGroup: null as { teamGroup: TeamGroup; user: User } | null,
    removeMemberFromTeamGroup: null as {
      teamGroup: TeamGroup;
      user: User;
    } | null,
    removeTeamGroup: null as TeamGroup | null,
    removeMemberFromTeam: null as User | null,
  });

  const [modalManageGroupLeaders, setModalManageGroupLeaders] =
    useState<TeamGroup | null>(null);

  const {
    isPending: isPendingTeams,
    data: teamsData,
    // error,
  } = useQuery({
    queryKey: ['Teams'],
    queryFn: getAllTeams,
    enabled: user?.role === 'admin',
  });

  const {
    isPending: isPendingTeamMembers,
    data: teamMembersData,
    // error,
  } = useQuery({
    queryKey: [
      'TeamMembers',
      selectedTeamOption?.value,
      membersTableState.pagination,
      membersTableState.filterEmail,
    ],
    queryFn: () =>
      getTeamMembers(selectedTeamOption?.value || '', {
        ...membersTableState.pagination,
        page: membersTableState.pagination.page + 1,
      }),
    enabled: !!selectedTeamOption?.value,
  });

  useEffect(() => {
    if (teamId && teamsData && user?.role === 'admin') {
      const team = teamsData.find((team) => team.id === teamId);
      if (team) {
        setSelectedTeamOption({
          value: team.id,
          label: `Team di ${team.teamManager.name}`,
        });
      }
    }

    if (teamId && user?.role !== 'admin') {
      setSelectedTeamOption({
        value: user?.team?.id || '',
        label: `Team `,
      });
    }
  }, [teamId, teamsData, user?.role, user?.team]);

  const {
    isPending: isPendingTeamGroups,
    data: teamGroups,
    // error,
  } = useQuery({
    queryKey: ['TeamGroups', selectedTeamOption?.value],
    queryFn: () => getAllTeamGroup(selectedTeamOption?.value || ''),
    enabled: !!selectedTeamOption?.value,
  });

  const changeCanReceiveLeadsMutation = useMutation({
    mutationFn: ({
      userId,
      canReceiveLeads,
    }: {
      userId: string;
      canReceiveLeads: boolean;
    }) => changeCanReceiveLeads({ userId, canReceiveLeads }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          'TeamMembers',
          selectedTeamOption?.value,
          membersTableState.pagination,
          membersTableState.filterEmail,
        ],
      });
      toast.success('Assegna Lead modificato!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const changeCanReceiveLeadsQuintoMutation = useMutation({
    mutationFn: ({
      userId,
      canReceiveLeadsQuinto,
    }: {
      userId: string;
      canReceiveLeadsQuinto: boolean;
    }) => changeCanReceiveLeadsQuinto({ userId, canReceiveLeadsQuinto }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          'TeamMembers',
          selectedTeamOption?.value,
          membersTableState.pagination,
          membersTableState.filterEmail,
        ],
      });
      toast.success('Assegna Lead Quinto modificato!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const changeCanReceiveLeadsPrestitoMutation = useMutation({
    mutationFn: ({
      userId,
      canReceiveLeadsPrestito,
    }: {
      userId: string;
      canReceiveLeadsPrestito: boolean;
    }) => changeCanReceiveLeadsPrestito({ userId, canReceiveLeadsPrestito }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          'TeamMembers',
          selectedTeamOption?.value,
          membersTableState.pagination,
          membersTableState.filterEmail,
        ],
      });
      toast.success('Assegna Lead Prestito modificato!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const createTeamGroupMutation = useMutation({
    mutationFn: ({
      teamId,
      teamGroupData,
    }: {
      teamId: string;
      teamGroupData: Omit<TeamGroup, 'id' | 'team'>;
    }) => createTeamGroupRequest(teamId, teamGroupData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          'TeamMembers',
          selectedTeamOption?.value,
          membersTableState.pagination,
          membersTableState.filterEmail,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ['TeamGroups', selectedTeamOption?.value],
      });
      toast.success('Gruppo creato!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const addMemberToGroupMutation = useMutation({
    mutationFn: ({
      teamGroupId,
      userId,
    }: {
      teamGroupId: string;
      userId: string;
    }) => addMemberToGroup(teamGroupId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          'TeamMembers',
          selectedTeamOption?.value,
          membersTableState.pagination,
          membersTableState.filterEmail,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ['TeamGroups', selectedTeamOption?.value],
      });
      toast.success('Membro aggiunto al gruppo!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const removeMemberFromGroupMutation = useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      removeMemberFromGroup(groupId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          'TeamMembers',
          selectedTeamOption?.value,
          membersTableState.pagination,
          membersTableState.filterEmail,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ['TeamGroups', selectedTeamOption?.value],
      });
      toast.success('Membro rimosso dal gruppo!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const removeTeamGroupMutation = useMutation({
    mutationFn: ({ groupId }: { groupId: string }) =>
      removeTeamGroupRequest(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          'TeamMembers',
          selectedTeamOption?.value,
          membersTableState.pagination,
          membersTableState.filterEmail,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ['TeamGroups', selectedTeamOption?.value],
      });
      toast.success('Gruppo rimosso!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const removeMemberFromTeamMutation = useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: string }) =>
      removeMemberFromTeam(teamId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          'TeamMembers',
          selectedTeamOption?.value,
          membersTableState.pagination,
          membersTableState.filterEmail,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ['TeamGroups', selectedTeamOption?.value],
      });
      toast.success('Utente rimosso dal team!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const sendNotificationAlertTeamMemberMutation = useMutation({
    mutationFn: ({
      teamId,
      teamMemberId,
    }: {
      teamId: string;
      teamMemberId: string;
    }) => sendNotificationAlertTeamMember(teamId, teamMemberId),
    onSuccess: () => {
      toast.success('Notifica inviata!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const onChangeSelectedTeam = (selectedTeamOption: {
    value: string;
    label: string;
  }) => {
    navigate(`/admin/teams/${selectedTeamOption.value}`);

    setSelectedTeamOption(selectedTeamOption);
    setMembersTableState({
      ...membersTableState,
      pagination: defaultPageModel,
    });
  };

  const onCanReceiveLeadsCheckboxChange = (row: User) => {
    setModalState((prev) => ({
      ...prev,
      userToChangeCanReceiveLeads: row,
    }));
  };

  const confirmChangeCanReceiveLeads = async () => {
    if (!modalState.userToChangeCanReceiveLeads) {
      return;
    }

    changeCanReceiveLeadsMutation.mutateAsync({
      userId: modalState.userToChangeCanReceiveLeads.id,
      canReceiveLeads: !modalState.userToChangeCanReceiveLeads.canReceiveLeads,
    });
  };

  const onCanReceiveLeadsQuintoCheckboxChange = (row: User) => {
    setModalState((prev) => ({
      ...prev,
      userToChangeCanReceiveLeadsQuinto: row,
    }));
  };

  const confirmChangeCanReceiveLeadsQuinto = async () => {
    if (!modalState.userToChangeCanReceiveLeadsQuinto) {
      return;
    }

    changeCanReceiveLeadsQuintoMutation.mutateAsync({
      userId: modalState.userToChangeCanReceiveLeadsQuinto.id,
      canReceiveLeadsQuinto: !modalState.userToChangeCanReceiveLeadsQuinto.canReceiveLeadsQuinto,
    });
  };

  const onCanReceiveLeadsPrestitoCheckboxChange = (row: User) => {
    setModalState((prev) => ({
      ...prev,
      userToChangeCanReceiveLeadsPrestito: row,
    }));
  };

  const confirmChangeCanReceiveLeadsPrestito = async () => {
    if (!modalState.userToChangeCanReceiveLeadsPrestito) {
      return;
    }

    changeCanReceiveLeadsPrestitoMutation.mutateAsync({
      userId: modalState.userToChangeCanReceiveLeadsPrestito.id,
      canReceiveLeadsPrestito: !modalState.userToChangeCanReceiveLeadsPrestito.canReceiveLeadsPrestito,
    });
  };

  const createTeamGroup = async (
    teamGroupData: Omit<TeamGroup, 'id' | 'team'>
  ) => {
    if (!selectedTeamOption?.value) {
      return;
    }

    await createTeamGroupMutation.mutateAsync({
      teamId: selectedTeamOption.value,
      teamGroupData,
    });
  };

  const onTeamGroupChange = (row: User, groupId: string) => {
    const teamGroup = teamGroups?.find((group) => group.id === groupId);
    if (!teamGroup) {
      return;
    }

    setModalState((prev) => ({
      ...prev,
      addMemberToTeamGroup: { teamGroup, user: row },
    }));
  };

  const confirmAddMemberToTeamGroup = async () => {
    if (!modalState.addMemberToTeamGroup) {
      return;
    }

    await addMemberToGroupMutation.mutateAsync({
      teamGroupId: modalState.addMemberToTeamGroup.teamGroup.id,
      userId: modalState.addMemberToTeamGroup.user.id,
    });
  };

  const onRemoveMemberFromTeamGroup = (row: User, groupId: string) => {
    const teamGroup = teamGroups?.find((group) => group.id === groupId);
    if (!teamGroup) {
      return;
    }

    setModalState((prev) => ({
      ...prev,
      removeMemberFromTeamGroup: { teamGroup, user: row },
    }));
  };

  const confirmRemoveMemberFromTeamGroup = async () => {
    if (!modalState.removeMemberFromTeamGroup) {
      return;
    }

    await removeMemberFromGroupMutation.mutateAsync({
      groupId: modalState.removeMemberFromTeamGroup.teamGroup.id,
      userId: modalState.removeMemberFromTeamGroup.user.id,
    });
  };

  const onRemoveMemberFromTeam = (row: User) => {
    setModalState((prev) => ({
      ...prev,
      removeMemberFromTeam: row,
    }));
  };

  const confirmRemoveMemberFromTeam = async () => {
    if (!modalState.removeMemberFromTeam) {
      return;
    }

    await removeMemberFromTeamMutation.mutateAsync({
      teamId: selectedTeamOption?.value || '',
      userId: modalState.removeMemberFromTeam.id,
    });
  };

  const onDeleteTeamGroup = (group: TeamGroup) => {
    setModalState((prev) => ({
      ...prev,
      removeTeamGroup: group,
    }));
  };

  const confirmRemoveTeamGroup = async () => {
    if (!modalState.removeTeamGroup) {
      return;
    }

    await removeTeamGroupMutation.mutateAsync({
      groupId: modalState.removeTeamGroup.id,
    });
  };

  const onConfirmSendAlertNotificationToMember = async (
    teamMember: TeamMember
  ) => {
    if (!teamId) return;
    await sendNotificationAlertTeamMemberMutation.mutateAsync({
      teamId,
      teamMemberId: teamMember.id,
    });
  };

  const getModalConfig = (modalState: ModalState): ModalConfig | null => {
    if (modalState.userToChangeCanReceiveLeads) {
      return {
        title: modalState.userToChangeCanReceiveLeads.canReceiveLeads
          ? 'Disabilita assegnazione Lead'
          : 'Abilita assegnazione Lead',
        description: `Sei sicuro di voler ${
          modalState.userToChangeCanReceiveLeads.canReceiveLeads
            ? 'disabilitare'
            : 'abilitare'
        } l'assegnazione Lead per ${
          modalState.userToChangeCanReceiveLeads.name
        }?`,
        onConfirm: confirmChangeCanReceiveLeads,
      };
    }

    if (modalState.userToChangeCanReceiveLeadsQuinto) {
      return {
        title: modalState.userToChangeCanReceiveLeadsQuinto.canReceiveLeadsQuinto
          ? 'Disabilita assegnazione Lead Quinto'
          : 'Abilita assegnazione Lead Quinto',
        description: `Sei sicuro di voler ${
          modalState.userToChangeCanReceiveLeadsQuinto.canReceiveLeadsQuinto
            ? 'disabilitare'
            : 'abilitare'
        } l'assegnazione Lead Quinto per ${
          modalState.userToChangeCanReceiveLeadsQuinto.name
        }?`,
        onConfirm: confirmChangeCanReceiveLeadsQuinto,
      };
    }

    if (modalState.userToChangeCanReceiveLeadsPrestito) {
      return {
        title: modalState.userToChangeCanReceiveLeadsPrestito.canReceiveLeadsPrestito
          ? 'Disabilita assegnazione Lead Prestito'
          : 'Abilita assegnazione Lead Prestito',
        description: `Sei sicuro di voler ${
          modalState.userToChangeCanReceiveLeadsPrestito.canReceiveLeadsPrestito
            ? 'disabilitare'
            : 'abilitare'
        } l'assegnazione Lead Prestito per ${
          modalState.userToChangeCanReceiveLeadsPrestito.name
        }?`,
        onConfirm: confirmChangeCanReceiveLeadsPrestito,
      };
    }

    if (modalState.addMemberToTeamGroup) {
      return {
        title: 'Aggiungi membro al gruppo',
        description: `Sei sicuro di voler aggiungere ${modalState.addMemberToTeamGroup.user.name} al gruppo ${modalState.addMemberToTeamGroup.teamGroup.name}?`,
        onConfirm: confirmAddMemberToTeamGroup,
      };
    }

    if (modalState.removeMemberFromTeamGroup) {
      return {
        title: 'Rimuovi membro dal gruppo',
        description: `Sei sicuro di voler rimuovere ${modalState.removeMemberFromTeamGroup.user.name} dal gruppo ${modalState.removeMemberFromTeamGroup.teamGroup.name}?`,
        onConfirm: confirmRemoveMemberFromTeamGroup,
      };
    }

    if (modalState.removeTeamGroup) {
      return {
        title: 'Rimuovi gruppo',
        description: `Sei sicuro di voler rimuovere il gruppo ${modalState.removeTeamGroup.name}?`,
        onConfirm: confirmRemoveTeamGroup,
      };
    }

    if (modalState.removeMemberFromTeam) {
      return {
        title: 'Rimuovi membro dal team',
        description: `Sei sicuro di voler rimuovere ${modalState.removeMemberFromTeam.name} dal ${selectedTeamOption?.label}?`,
        onConfirm: confirmRemoveMemberFromTeam,
      };
    }

    return null;
  };

  const teamsSelectOptions = teamsData?.map((team) => ({
    value: team.id,
    label: `Team di ${team.teamManager.name}`,
  }));

  const selectedTeam =
    user?.role === 'admin'
      ? teamsData?.find((team) => team.id === selectedTeamOption?.value)
      : user?.team;

  const modalConfig = getModalConfig(modalState);

  return {
    selectedTeam,
    selectedTeamOption,
    teamsSelectOptions,
    onChangeSelectedTeam,
    membersTableState,
    setMembersTableState,
    isPendingTeams: isPendingTeams && user?.role === 'admin',
    isPendingTeamMembers,
    teamMembersData,
    onCanReceiveLeadsCheckboxChange,
    onCanReceiveLeadsQuintoCheckboxChange,
    onCanReceiveLeadsPrestitoCheckboxChange,
    modalConfig,
    setModalState,
    createTeamGroup,
    isPendingTeamGroups,
    teamGroups: teamGroups || [],
    onTeamGroupChange,
    onRemoveMemberFromTeamGroup,
    onDeleteTeamGroup,
    modalManageGroupLeaders,
    setModalManageGroupLeaders,
    onRemoveMemberFromTeam,
    user,
    onConfirmSendAlertNotificationToMember,
    showModalAlertNotify,
    setShowModalAlertNotify,
  };
};

export default useTeamsDetailPage;
